import { memo, useCallback, useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, Pencil, Plus, Trash } from 'lucide-react';
import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { Button } from '@/shared/components/ui/button';
import { CustomModal } from '@/shared/components/ui/modal';
import ListItem from '@/features/pages/components/layout/Sidebar/PageList/ListItem';
import { DisplayedCollectionSection, Section } from '../../types/collection.types';
import CollectionTitle from './Title';
import { useCollectionStore } from '../../stores/useCollectionStore';

// Container for multiple sections
const NestedListContainer = ({
  displayedSections,
  onUpdate,
  disabled = false,
  onArchiveSection,
  onAddSectionPage,
}: {
  displayedSections: DisplayedCollectionSection[];
  onUpdate: (updates: DisplayedCollectionSection[]) => Promise<void>;
  disabled?: boolean;
  onAddSectionPage: (sectionId: string, orderNumber: number) => Promise<void>;
  onArchiveSection: (sectionId: string) => Promise<void>;
}) => {
  const [currentSection, setCurrentSection] = useState<Section | null>(null);
  const { closedSections, toggleSection } = useCollectionStore();

  const [sortedSections, setSortedSections] = useState<DisplayedCollectionSection[]>(
    [...displayedSections].sort((a, b) => a.section.orderNumber - b.section.orderNumber)
  );

  // Toggle section expand/collapse
  const handleToggleSection = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering drag when clicking the toggle
    toggleSection(sectionId);
  };

  // Handle drag end for both sections and pages
  const onDragEnd = useCallback((result: DropResult) => {
    if (!result.destination) return;

    const { source, destination, type } = result;

    // Handle section reordering
    if (type === 'SECTION') {
      setSortedSections(prevSections => {
        const newSections = reorderSections(prevSections, source.index, destination.index);
        void onUpdate(newSections);
        return newSections;
      });
      return;
    }

    // Handle page dragging
    const sourceSectionId = source.droppableId.replace('section-', '');
    const destSectionId = destination.droppableId.replace('section-', '');

    if (sourceSectionId === destSectionId) {
      // Same section page reordering
      setSortedSections(prevSections => {
        const newSections = reorderPagesInSection(
          prevSections,
          sourceSectionId,
          source.index,
          destination.index
        );
        void onUpdate(newSections);
        return newSections;
      });
    } else {
      // Cross-section page moving
      setSortedSections(prevSections => {
        const newSections = movePageBetweenSections(
          prevSections,
          sourceSectionId,
          destSectionId,
          source.index,
          destination.index
        );
        void onUpdate(newSections);
        return newSections;
      });
    }
  }, []);

  return (
    <>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="sections" type="SECTION">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="w-full space-y-2">
              {sortedSections.map((displayedSection, index) => (
                <Draggable
                  key={displayedSection.section.id}
                  draggableId={`section-draggable-${displayedSection.section.id}`}
                  index={index}
                  isDragDisabled={disabled}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        opacity: snapshot.isDragging ? 0.7 : 1,
                      }}
                      className="mb-2 pl-2 min-w-[262px]" // prevent collapsing when toggling sidemenu open/close
                    >
                      {/* Section header */}
                      <div
                        className="flex items-center px-2.5 pr-4 py-1 cursor-pointer rounded w-full group"
                        {...provided.dragHandleProps}
                        onClick={e => {
                          handleToggleSection(displayedSection.section.id, e);
                        }}
                      >
                        <div className="flex flex-grow gap-2 items-center">
                          <span className="cursor-pointer">
                            {!closedSections[displayedSection.section.id] ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </span>
                          <h3 className="font-semibold text-[15px] text-wrap">
                            {displayedSection.section.title}
                          </h3>
                        </div>
                        {!disabled && (
                          <div className="gap-2 flex items-center">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6 opacity-20 hover:opacity-100"
                              title="Edit section title"
                              onClick={e => {
                                e.stopPropagation();
                                setCurrentSection(displayedSection.section);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Delete section"
                              className="w-6 h-6 opacity-20 hover:opacity-100"
                              onClick={e => {
                                e.stopPropagation();
                                void onArchiveSection(displayedSection.section.id);
                              }}
                            >
                              <Trash className="h-3 w-3" />
                            </Button>{' '}
                          </div>
                        )}
                      </div>

                      {/* Pages list - only render if section is not closed */}
                      {!closedSections[displayedSection.section.id] && (
                        <>
                          <Droppable
                            droppableId={`section-${displayedSection.section.id}`}
                            type="PAGE"
                          >
                            {provided => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className="w-full space-y-1 pl-2 pr-2.5 mt-1"
                              >
                                {displayedSection.section.pages
                                  .sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
                                  .map((page, pageIndex) => (
                                    <Draggable
                                      key={page.id}
                                      draggableId={page.id}
                                      index={pageIndex}
                                      isDragDisabled={disabled}
                                    >
                                      {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                            opacity: snapshot.isDragging ? 0.5 : 1,
                                          }}
                                        >
                                          <ListItem metadata={page} />
                                        </div>
                                      )}
                                    </Draggable>
                                  ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                          {!disabled && (
                            <div className="mx-4 mt-1">
                              <div
                                onClick={() =>
                                  void onAddSectionPage(
                                    displayedSection.section.id,
                                    displayedSection.section.pages.length
                                  )
                                }
                                className="w-full font-normal text-[14px] rounded flex items-center justify-start gap-2 hover:cursor-pointer hover:bg-accent p-2 py-2 mb-4"
                              >
                                <Plus className="h-4 w-4" />
                                <span>Add page</span>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <EditSectionModal
        isOpen={!!currentSection}
        section={currentSection}
        sections={displayedSections}
        onClose={() => {
          setCurrentSection(null);
        }}
        onUpdate={onUpdate}
      />
    </>
  );
};

export default memo(NestedListContainer);

const EditSectionModal: React.FC<{
  section: Section | null;
  isOpen: boolean;
  onClose: () => void;
  sections: DisplayedCollectionSection[];
  onUpdate: (updates: DisplayedCollectionSection[]) => Promise<void>;
}> = ({ section, sections, isOpen, onClose, onUpdate }) => {
  const [title, setTitle] = useState('');

  // Update local title state when section changes
  useEffect(() => {
    if (section) {
      setTitle(section.title || 'Untitled Section');
    }
  }, [section]);

  const updateSection = useCallback(async () => {
    if (!section) return;

    const newSections = sections.map(s =>
      s.section.id === section.id ? { ...s, section: { ...s.section, title } } : s
    );

    await onUpdate(newSections);
    onClose();
  }, [section, sections, onUpdate, title, onClose]);

  return (
    <CustomModal isOpen={isOpen} onClose={onClose}>
      <div className="pt-2 px-4 pb-4">
        <h4 className="pl-1">Update Section Title</h4>
        <CollectionTitle
          title={title}
          placeholder="Enter a title for this section"
          onComplete={updateSection}
          onChange={setTitle}
        />
        <small className="pl-1">Click "Enter" to submit</small>
      </div>
    </CustomModal>
  );
};

// Pure function to reorder sections
const reorderSections = (
  sections: DisplayedCollectionSection[],
  sourceIndex: number,
  destinationIndex: number
): DisplayedCollectionSection[] => {
  const result = [...sections];
  const [removed] = result.splice(sourceIndex, 1);
  result.splice(destinationIndex, 0, removed);

  // Update orderNumber for all sections
  result.forEach((section, index) => {
    section.section.orderNumber = index;
  });

  return result;
};

// Pure function to reorder pages within the same section
const reorderPagesInSection = (
  sections: DisplayedCollectionSection[],
  sectionId: string,
  sourceIndex: number,
  destinationIndex: number
): DisplayedCollectionSection[] => {
  return sections.map(section => {
    if (section.section.id === sectionId) {
      // Create a copy of pages array
      let pages = [...section.section.pages];
      const [removed] = pages.splice(sourceIndex, 1);
      pages.splice(destinationIndex, 0, removed);

      // Update orderNumber values
      pages = pages.map((page, index) => ({ ...page, orderNumber: index }));

      // Return updated section
      return {
        ...section,
        section: {
          ...section.section,
          pages,
        },
      };
    }
    return section;
  });
};

// Pure function to move a page between sections
const movePageBetweenSections = (
  sections: DisplayedCollectionSection[],
  sourceSectionId: string,
  destinationSectionId: string,
  sourceIndex: number,
  destinationIndex: number
): DisplayedCollectionSection[] => {
  // Find source and destination sections
  const sourceSection = sections.find(s => s.section.id === sourceSectionId);
  const destSection = sections.find(s => s.section.id === destinationSectionId);

  if (!sourceSection || !destSection) return sections;

  // Get the page being moved
  const sourcePage = sourceSection.section.pages[sourceIndex];

  // Create updated page with new sectionId
  const updatedPage = {
    ...sourcePage,
    sectionId: destinationSectionId,
  };

  return sections.map(section => {
    // Update source section (remove page)
    if (section.section.id === sourceSectionId) {
      const newPages = [...section.section.pages];
      newPages.splice(sourceIndex, 1);

      // Update orderNumbers
      newPages.forEach((page, index) => {
        page.orderNumber = index;
      });

      return {
        ...section,
        section: {
          ...section.section,
          pages: newPages,
        },
      };
    }

    // Update destination section (add page)
    if (section.section.id === destinationSectionId) {
      const newPages = [...section.section.pages];
      newPages.splice(destinationIndex, 0, updatedPage);

      // Update orderNumbers
      newPages.forEach((page, index) => {
        page.orderNumber = index;
      });

      return {
        ...section,
        section: {
          ...section.section,
          pages: newPages,
        },
      };
    }

    return section;
  });
};
